import { BLOG_OPEN, toDisplay, type Ref } from "@bibleunleashed/shared";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { useUser } from "@/lib/auth";
import { PassagePicker } from "@/reader/PassagePicker";
import { Icon } from "./Icon";
import { ThemeMenu } from "./ThemeMenu";

const navLink = "text-ink-2 hover:text-ink text-[13px]";
const menuLink = "block px-4 py-2.5 text-[15px] text-ink-2 hover:bg-paper-2 hover:text-ink";

const SECTIONS = [
  { href: "/commentary", label: "Commentary" },
  { href: "/bibliography", label: "Bibliography" },
  { href: "/glossary", label: "Glossary" },
  { href: "/counseling", label: "Counseling" },
  { href: "/resources", label: "Resources" },
  ...(BLOG_OPEN ? [{ href: "/blog", label: "Blog" }] : []),
  { href: "/about", label: "About" },
  { href: "/privacy", label: "Privacy" },
];

export function Brand() {
  return (
    <Link href="/" className="flex shrink-0 items-center whitespace-nowrap">
      <span className="flex sm:hidden">
        <img src="/logo-icon.png" alt="The Bible Unleashed" className="brand-logo-light h-8 w-auto" />
        <img src="/logo-icon-dark.png" alt="The Bible Unleashed" className="brand-logo-dark h-8 w-auto" />
      </span>
      <span className="hidden sm:flex">
        <img src="/logo.png" alt="The Bible Unleashed" className="brand-logo-light h-9 w-auto" />
        <img src="/logo-dark.png" alt="The Bible Unleashed" className="brand-logo-dark h-9 w-auto" />
      </span>
    </Link>
  );
}

export function Header({ current, onToggleSettings, onToggleTools }: { current?: Ref; onToggleSettings?: () => void; onToggleTools?: () => void }) {
  const { data: user } = useUser();
  const [pickerOpen, setPickerOpen] = useState(false);
  const closePicker = useCallback(() => setPickerOpen(false), []);
  const chapter = current ? { book: current.book, chapter: current.chapter } : { book: "Gen", chapter: 1 };
  // Below md the section links and the account link live behind a menu button:
  // a phone's header has room for the brand, the picker and the icon buttons only.
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);
  const bar = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setMenuOpen(false);
    const onClick = (e: MouseEvent) => {
      if (bar.current && !e.composedPath().includes(bar.current)) setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const t = setTimeout(() => document.addEventListener("click", onClick), 0);
    return () => {
      document.removeEventListener("keydown", onKey);
      clearTimeout(t);
      document.removeEventListener("click", onClick);
    };
  }, [menuOpen]);

  return (
    <header ref={bar} className="relative flex h-14 items-center gap-2 border-b border-rule bg-paper px-4">
      <Brand />
      <nav className="ml-5 hidden gap-4 md:flex">
        {SECTIONS.map((s) => (
          <Link key={s.href} href={s.href} className={navLink}>{s.label}</Link>
        ))}
      </nav>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setPickerOpen((v) => !v);
        }}
        aria-haspopup="dialog"
        aria-expanded={pickerOpen}
        className="ml-auto flex h-[34px] min-w-0 items-center gap-2 rounded-md border border-rule bg-paper-2 px-3 font-serif text-[15px] text-ink hover:border-ink-3 sm:min-w-[220px]"
      >
        <span className="truncate">{current ? toDisplay(chapter) : "Go to a chapter"}</span>
        <Icon name="chevron" className="ml-auto shrink-0 rotate-90 text-ink-3" width="14" height="14" />
      </button>
      {pickerOpen && <PassagePicker current={chapter} onClose={closePicker} />}
      {onToggleSettings && (
        <button onClick={(e) => { e.stopPropagation(); onToggleSettings(); }} className="grid size-[34px] place-items-center rounded-md font-serif text-[15px] font-semibold text-ink-2 hover:bg-paper-2 hover:text-ink" aria-label="Text settings">
          Aa
        </button>
      )}
      {onToggleTools && (
        <button onClick={onToggleTools} className="grid size-[34px] place-items-center rounded-md text-ink-2 hover:bg-paper-2 hover:text-ink" aria-label="Study tools">
          <Icon name="panel" />
        </button>
      )}
      <ThemeMenu />
      {user ? (
        <Link
          href="/account"
          title={`${user.name} · account`}
          className="ml-1 hidden size-[30px] place-items-center rounded-full bg-accent text-xs font-semibold text-white md:grid"
        >
          {user.name.slice(0, 1).toUpperCase()}
        </Link>
      ) : (
        <Link href="/login" className="ml-1 hidden rounded-md border border-ink px-3 py-1.5 text-[13px] whitespace-nowrap text-ink md:block">Sign in</Link>
      )}
      <button
        onClick={() => setMenuOpen((v) => !v)}
        aria-expanded={menuOpen}
        aria-controls="site-menu"
        aria-label="Menu"
        className="grid size-[34px] shrink-0 place-items-center rounded-md text-ink-2 hover:bg-paper-2 hover:text-ink md:hidden"
      >
        <Icon name={menuOpen ? "close" : "menu"} />
      </button>
      {menuOpen && (
        <nav id="site-menu" aria-label="Site" className="absolute inset-x-0 top-full z-30 border-b border-rule bg-paper py-1 shadow-pop md:hidden">
          {SECTIONS.map((s) => (
            <Link key={s.href} href={s.href} onClick={closeMenu} className={menuLink}>{s.label}</Link>
          ))}
          <div className="mx-4 my-1 border-t border-rule" />
          {user ? (
            <Link href="/account" onClick={closeMenu} className={menuLink}>Account · {user.name}</Link>
          ) : (
            <Link href="/login" onClick={closeMenu} className={menuLink}>Sign in</Link>
          )}
        </nav>
      )}
    </header>
  );
}
